/**
 * 「计时到预计时长了」那一声提示音的**合成与播放**。
 *
 * ## 为什么用 Web Audio 合成，而不是 `speechSynthesis` 说一句
 *
 * 两条都试得起（都不用音频文件），但这里选 **Web Audio 合成一小段双音**：
 *
 *  - `speechSynthesis` 的**声音取决于设备**（有没有装中文语音、是哪个引擎），
 *    同一句"时间到"在这台手机上是温柔女声、在那台平板上是机械音、在没装语音包的
 *    浏览器上**干脆不出声**；Web Audio 是浏览器内置的振荡器，哪里都一样。
 *  - 说话**天然比"叮"两声长**（一句中文至少一秒多），而这是个鼓励孩子学习的
 *    产品，不是闹钟 —— 温和、短促、不打断思路更合适。
 *  - 合成音的音高/音量/包络我可以精确控制（见下面的参数），也就可控地"温和"。
 *
 * ## 温和是怎么做到的（不是警报器）
 *
 *  - 两个**正弦**音（不是方波/锯齿）上行：D5 (587.33Hz) → A5 (880Hz)，五度，
 *    听起来是"叮—咚"而不是"哔哔"；
 *  - 峰值音量只有 **0.12**（满量程的 12%），且每个音都有 30ms 的**渐入**与
 *    指数**渐出**，不会有"啪"的爆音；
 *  - 整声**约 0.6 秒**，响完就结束，不循环、不重复。
 *
 * 具体参数在 `softChime.ts`（那一份可以拿假的 `AudioContext` 直接测）。
 *
 * ## 自动播放限制怎么处理（被拒绝时**绝不影响孩子**）
 *
 * 浏览器只在**用户手势之后**才允许出声。这里两层应对：
 *
 *  1. `primeEstimateChimeAudio()` 由「开始计时」按钮的点击调用 ——
 *     手势里就把 `AudioContext` 建好并 `resume()`（iOS 上这步是必须的，
 *     否则二十分钟后那一声会被静默拒绝）；
 *  2. `playEstimateChime()` 自己也会 `resume()`，并把**所有**失败
 *     （构造失败、`resume()` 被拒绝、合成异常）**吞掉**：
 *     提示音是锦上添花，不能因为放不出来就弹错误、写控制台、打扰孩子。
 */
import { playSoftChime } from './softChime';

type AudioContextConstructor = typeof AudioContext;

/** 惰性创建的 `AudioContext`（一个页面一个就够，见 `getOrCreateAudioContext`）。 */
let cachedContext: AudioContext | null = null;

/**
 * 拿到音频上下文构造函数。
 *
 * 老 Safari 只有 `webkitAudioContext`；没有 Web Audio 的环境（Node、很老的浏览器）
 * 返回 `null` —— 调用方据此**静默降级**（没有声音，没有报错）。
 */
function resolveAudioContextConstructor(): AudioContextConstructor | null {
  if (typeof window === 'undefined') return null;
  const scope = window as typeof window & { webkitAudioContext?: AudioContextConstructor };
  return scope.AudioContext ?? scope.webkitAudioContext ?? null;
}

/**
 * 取（必要时建）音频上下文。建不出来就返回 `null`。
 *
 * 复用同一个上下文的原因：iOS 对同时存在的 `AudioContext` 数量有上限，
 * 而"每响一次建一个"在长时间使用后会走到那个上限 —— 那时连声音都放不出来了。
 */
function getOrCreateAudioContext(): AudioContext | null {
  if (cachedContext) return cachedContext;
  const Constructor = resolveAudioContextConstructor();
  if (!Constructor) return null;
  try {
    cachedContext = new Constructor();
  } catch {
    // 例如上下文数量已达上限：不抛给调用方
    return null;
  }
  return cachedContext;
}

/**
 * 在手势里"解锁"音频（调用点：**「开始计时」按钮的点击处理**）。
 *
 * 做两件事，缺一不可：
 *  - `resume()`：上下文可能因自动播放策略处于 suspended；
 *  - 播一段**听不见的**极短音频：部分 iOS 版本要求"真的播过一次"才算解锁。
 *
 * ⛔ 不返回任何东西、不抛任何错 —— 解锁失败只意味着到时候没声音。
 */
export function primeEstimateChimeAudio(): void {
  const context = getOrCreateAudioContext();
  if (!context) return;
  try {
    void context.resume().catch(() => {});
    const silent = context.createBufferSource();
    silent.buffer = context.createBuffer(1, 1, 22_050);
    silent.connect(context.destination);
    silent.start(0);
  } catch {
    // 解锁失败不是错误，只是到时候没声音
  }
}

/**
 * 放一声"到点了"。
 *
 * 只在判据（`@fht/shared` 的 `advanceEstimateChime`）说该响的时候调用，
 * 一声 = 一小段上行的双音。**任何失败都不冒泡**。
 */
export function playEstimateChime(): void {
  const context = getOrCreateAudioContext();
  if (!context) return;
  try {
    // 被自动播放策略拒绝时这里会 reject —— 吞掉即可（见文件头）
    void context.resume().catch(() => {});
    playSoftChime(context);
  } catch {
    // 同上：放不出来就算了，绝不打扰孩子
  }
}
